import React from "react";

import {
  deleteAsset,
  getUploadedAssets,
  postAssetsUpload,
} from "../../../services/api-clinic";
import type { TSession } from "../../../utils/types";
import { normalizeClinicAssets, reconcileClinicAssets } from "./clinicAssets";
import type { TNormalizedClinicAsset } from "./clinicAssets";

type TUseClinicAssetsOptions = {
  session?: TSession | null;
  initialLoading?: boolean;
};

const getOperationError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

export const useClinicAssets = ({
  session,
  initialLoading = false,
}: TUseClinicAssetsOptions) => {
  const sessionKey = session
    ? `${session.userId}:${session.clinicId ?? "no-clinic"}`
    : null;
  const activeSessionKeyRef = React.useRef(sessionKey);
  const loadRequestIdRef = React.useRef(0);
  const addedAssetsRef = React.useRef(
    new Map<number, TNormalizedClinicAsset>(),
  );
  const removedAssetIdsRef = React.useRef(new Set<number>());
  const [assetState, setAssetState] = React.useState<{
    sessionKey: string | null;
    assets: TNormalizedClinicAsset[];
  }>({ sessionKey, assets: [] });
  const [isLoading, setIsLoading] = React.useState(initialLoading);
  const [isUploading, setIsUploading] = React.useState(false);
  const [deletingAssetId, setDeletingAssetId] = React.useState<number | null>(
    null,
  );
  const [error, setError] = React.useState<Error | null>(null);
  const assets = assetState.sessionKey === sessionKey ? assetState.assets : [];

  if (activeSessionKeyRef.current !== sessionKey) {
    activeSessionKeyRef.current = sessionKey;
    loadRequestIdRef.current += 1;
    addedAssetsRef.current.clear();
    removedAssetIdsRef.current.clear();
  }

  React.useEffect(() => {
    loadRequestIdRef.current += 1;
    setAssetState({ sessionKey, assets: [] });
    setIsLoading(initialLoading);
    setIsUploading(false);
    setDeletingAssetId(null);
    setError(null);
  }, [initialLoading, sessionKey]);

  const clearError = React.useCallback(() => setError(null), []);

  const load = React.useCallback(async () => {
    const requestedSessionKey = sessionKey;
    const requestId = ++loadRequestIdRef.current;
    setError(null);
    if (!session) {
      setIsLoading(false);
      return [];
    }

    setIsLoading(true);
    try {
      const response = await getUploadedAssets(session);
      const normalizedAssets = normalizeClinicAssets(response?.assets);
      const reconciledAssets = reconcileClinicAssets({
        loadedAssets: normalizedAssets,
        addedAssets: addedAssetsRef.current.values(),
        removedAssetIds: removedAssetIdsRef.current,
      });

      if (
        activeSessionKeyRef.current === requestedSessionKey &&
        loadRequestIdRef.current === requestId
      ) {
        setAssetState({
          sessionKey: requestedSessionKey,
          assets: reconciledAssets,
        });
      }
      return reconciledAssets;
    } catch (caughtError) {
      const operationError = getOperationError(caughtError);
      if (
        activeSessionKeyRef.current === requestedSessionKey &&
        loadRequestIdRef.current === requestId
      ) {
        setError(operationError);
      }
      throw operationError;
    } finally {
      if (
        activeSessionKeyRef.current === requestedSessionKey &&
        loadRequestIdRef.current === requestId
      ) {
        setIsLoading(false);
      }
    }
  }, [session, sessionKey]);

  const upload = React.useCallback(
    async (file: Blob) => {
      setError(null);
      if (!session) {
        const operationError = new Error("Session is not available.");
        setError(operationError);
        throw operationError;
      }

      setIsUploading(true);
      try {
        const response = await postAssetsUpload(session, { imageFile: file });
        const uploadedAsset: TNormalizedClinicAsset = {
          asset_id: response.asset_id,
          get_url: response.get_url,
          s3_key: response.s3_key,
        };
        if (activeSessionKeyRef.current !== sessionKey) {
          throw new Error("The active clinic changed during the upload.");
        }

        removedAssetIdsRef.current.delete(uploadedAsset.asset_id);
        addedAssetsRef.current.set(uploadedAsset.asset_id, uploadedAsset);
        setAssetState((current) => {
          const currentAssets =
            current.sessionKey === sessionKey ? current.assets : [];
          return {
            sessionKey,
            assets: [
              ...currentAssets.filter(
                (asset) => asset.asset_id !== uploadedAsset.asset_id,
              ),
              uploadedAsset,
            ],
          };
        });
        return uploadedAsset;
      } catch (caughtError) {
        const operationError = getOperationError(caughtError);
        if (activeSessionKeyRef.current === sessionKey) {
          setError(operationError);
        }
        throw operationError;
      } finally {
        if (activeSessionKeyRef.current === sessionKey) {
          setIsUploading(false);
        }
      }
    },
    [session, sessionKey],
  );

  const remove = React.useCallback(
    async (assetId: number) => {
      setError(null);
      if (!session) {
        const operationError = new Error("Session is not available.");
        setError(operationError);
        throw operationError;
      }

      setDeletingAssetId(assetId);
      try {
        await deleteAsset(session, assetId);
        if (activeSessionKeyRef.current !== sessionKey) {
          throw new Error("The active clinic changed during deletion.");
        }

        addedAssetsRef.current.delete(assetId);
        removedAssetIdsRef.current.add(assetId);
        setAssetState((current) => ({
          sessionKey,
          assets:
            current.sessionKey === sessionKey
              ? current.assets.filter((asset) => asset.asset_id !== assetId)
              : [],
        }));
      } catch (caughtError) {
        const operationError = getOperationError(caughtError);
        if (activeSessionKeyRef.current === sessionKey) {
          setError(operationError);
        }
        throw operationError;
      } finally {
        if (activeSessionKeyRef.current === sessionKey) {
          setDeletingAssetId(null);
        }
      }
    },
    [session, sessionKey],
  );

  return {
    assets,
    isLoading,
    isUploading,
    deletingAssetId,
    error,
    clearError,
    load,
    upload,
    remove,
  };
};
